/**
 * Calm Mode — a "no-stimulus" accessibility toggle, wired to a third analog
 * switch in the TV bezel (next to music + SFX).
 *
 * When ON:
 *  - Music and SFX volume are reduced by 60% (× CALM_AUDIO_FACTOR) — see SFX.ts
 *    and Music.ts, which read isCalmMode() when computing playback volume.
 *  - All camera shakes/flashes are suppressed globally — see calm-camera-guard.ts.
 *  - The full-screen edge-pulse strobe is skipped — see JuiceEffects/widgets.
 *
 * It does NOT mute audio and is fully independent of the music/SFX toggles.
 * Defaults to OFF so the baseline experience is unchanged.
 *
 * This module imports nothing from the audio layer — Music.ts subscribes via
 * onCalmModeChange() to re-apply its volume live, keeping the dependency
 * one-directional (no import cycle).
 */

/** Multiplier applied to music + SFX volume while Calm Mode is on (−60%). */
export const CALM_AUDIO_FACTOR = 0.4;

const STORAGE_KEY = 'snake_calm_mode';

// Default OFF: only on when explicitly persisted as '1' (note: '1', not '!== 0',
// the opposite default from the music/SFX switches which start enabled).
let enabled: boolean = (() => {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; }
  catch { return false; }
})();

const listeners: Set<(on: boolean) => void> = new Set();

export function setCalmMode(on: boolean): void {
  if (enabled === on) return;
  enabled = on;
  try { localStorage.setItem(STORAGE_KEY, on ? '1' : '0'); } catch { /* ignore */ }
  // Notify subscribers (the bezel switch syncs its UI; Music.ts re-applies its
  // volume live so the change is heard without cutting the current track).
  for (const cb of listeners) cb(on);
}

export function isCalmMode(): boolean { return enabled; }

/** Subscribe to Calm Mode changes (returns an unsubscribe function). */
export function onCalmModeChange(cb: (on: boolean) => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}
